import React, { Fragment } from "react";
import { Head } from "next/document";
import theme from "../theme/palette";
import brand from "../static/text/brand";
import link from "../static/text/link";
import { getRobotsMetaContent, getOgLocale } from "../helpers/seo";

export const DEFAULT_BROWSER_THEME_COLOR = "#403DE3";
const DEFAULT_SOCIAL_URL = "https://myfitsociety.com";
/** Public web GTM container (Google Ads + GA4 destinations live in GTM UI). */
export const DEFAULT_GTM_CONTAINER_ID = "GTM-WJR8VVN";

// Merek untuk meta sosial: nama pendek, bukan judul panjang di brand.mobile.name.
const SITE_NAME = "Myfitsociety";
const TWITTER_HANDLE = "@myfitsociety";

const getSocialUrl = canonicalUrl => canonicalUrl || brand.mobile.domain || DEFAULT_SOCIAL_URL;

// og:image/twitter:image WAJIB absolute — scraper (Facebook, Twitter/X, AI
// agents) tidak me-resolve path relatif terhadap halaman.
export const toAbsoluteAssetUrl = (src) => {
  const value = `${src || ""}`;
  if (!value) return value;
  return /^https?:\/\//i.test(value) ? value : `${DEFAULT_SOCIAL_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

// JSON-LD sitewide: Organization + WebSite + MobileApplication (+ WebPage per
// halaman). Hanya di-render pada halaman indexable (host produksi) supaya
// staging tidak mengiklankan URL produksi dalam structured data. @id node
// sitewide stabil dan sama di semua halaman, jadi mesin pencari/AI menyatukan
// entitasnya; hanya node WebPage yang berbeda per halaman.
const ORGANIZATION_ID = `${DEFAULT_SOCIAL_URL}/#organization`;
const WEBSITE_ID = `${DEFAULT_SOCIAL_URL}/#website`;
const APP_ID = `${DEFAULT_SOCIAL_URL}/#app`;
const SITE_DESCRIPTION = "Myfitsociety is a fitness and diet mobile app for Android and iOS with workouts, diet programs, calorie tracking, an AI Food Scanner, an AI Coach, healthy recipes, and meditation.";
const SUPPORT_EMAIL = "support@myfitsociety.com";

// Bahasa halaman: htmlLang dari _document, atau dari prefix /id di canonical url.
const resolvePageLang = (canonicalUrl, htmlLang) => htmlLang
  || (/^https?:\/\/[^/]+\/id(\/|$)/.test(`${canonicalUrl || ""}`) ? "id" : "en");

export const buildSiteJsonLd = (canonicalUrl, htmlLang) => {
  const logoUrl = `${DEFAULT_SOCIAL_URL}/static/favicons/web-app-manifest-512x512.png`;
  const imageUrl = toAbsoluteAssetUrl(brand.mobile.ogImg);
  const graph = [
    {
      "@type": "Organization",
      "@id": ORGANIZATION_ID,
      name: "Myfitsociety",
      url: `${DEFAULT_SOCIAL_URL}/`,
      description: SITE_DESCRIPTION,
      logo: {
        "@type": "ImageObject",
        url: logoUrl,
        width: 512,
        height: 512,
      },
      image: imageUrl,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: SUPPORT_EMAIL,
          url: `${DEFAULT_SOCIAL_URL}${link.mobile.contactUs}`,
          availableLanguage: ["English", "Indonesian"],
        },
      ],
      sameAs: [
        link.mobile.mfsFacebook,
        link.mobile.mfsFacebookId,
        link.mobile.mfsInstagram,
        link.mobile.mfsInstagramId,
        link.mobile.mfsTwitter,
        link.mobile.mfsPinterest,
      ],
    },
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: `${DEFAULT_SOCIAL_URL}/`,
      name: "Myfitsociety",
      publisher: { "@id": ORGANIZATION_ID },
      inLanguage: ["en", "id"],
    },
    {
      "@type": "MobileApplication",
      "@id": APP_ID,
      name: "Myfitsociety",
      // Selalu root situs: entitas aplikasi yang sama di setiap halaman.
      url: `${DEFAULT_SOCIAL_URL}/`,
      description: SITE_DESCRIPTION,
      image: imageUrl,
      publisher: { "@id": ORGANIZATION_ID },
      operatingSystem: ["Android 9+", "iOS 13+"],
      applicationCategory: "HealthApplication",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      installUrl: [link.mobile.playStore, link.mobile.appStore.split("?")[0]],
    },
  ];
  if (canonicalUrl) {
    const lang = resolvePageLang(canonicalUrl, htmlLang);
    graph.push({
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      inLanguage: lang,
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": APP_ID },
    });
  }
  // "<" escaped: canonicalUrl comes from the request path and this string is
  // inlined into a <script> tag.
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": graph,
  }).replace(/</g, "\\u003c");
};

/**
 * Resolve Web GTM container id.
 * - NEXT_PUBLIC_GTM_ID set → use it
 * - NEXT_PUBLIC_GTM_ID="" → disabled (no inject)
 * - unset → DEFAULT_GTM_CONTAINER_ID
 * Does NOT depend solely on API_URL===be.myfitsociety.com (that gate blocked prod HTML).
 */
export const resolveGtmContainerId = () => {
  const raw = process.env.NEXT_PUBLIC_GTM_ID;
  if (raw === "") {
    return null;
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return DEFAULT_GTM_CONTAINER_ID;
};

/**
 * Whether to inject GTM snippet.
 * Disable: NEXT_PUBLIC_GTM_ENABLED=false|0, or NEXT_PUBLIC_GTM_ID=""
 * Force enable (staging/local or CI): NEXT_PUBLIC_GTM_ENABLED=true|1
 *
 * MEDIUM9: do NOT auto-enable for every NODE_ENV=production build (staging prod-mode
 * would pollute the prod GTM container). Enable only via explicit flag/ID, prod
 * backend API_URL, known public site URL env, or runtime hostname myfitsociety.com.
 */
export const shouldInjectGtm = () => {
  const enabledFlag = process.env.NEXT_PUBLIC_GTM_ENABLED;
  if (enabledFlag === "false" || enabledFlag === "0") {
    return false;
  }

  const containerId = resolveGtmContainerId();
  if (!containerId) {
    return false;
  }

  if (enabledFlag === "true" || enabledFlag === "1") {
    return true;
  }

  // Explicit container override implies intent to load when not disabled.
  if (typeof process.env.NEXT_PUBLIC_GTM_ID === "string" && process.env.NEXT_PUBLIC_GTM_ID.trim()) {
    return true;
  }

  // Legacy prod-backend gate (build-time).
  if (process.env.API_URL === "https://be.myfitsociety.com") {
    return true;
  }

  // Build-time public site URL allowlist.
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL
    || process.env.NEXT_PUBLIC_WEB_URL
    || process.env.NEXT_PUBLIC_APP_URL
    || "";
  if (typeof siteUrl === "string" && /myfitsociety\.com/i.test(siteUrl)) {
    return true;
  }

  // Runtime client host (covers prod even if build-time API_URL was wrong).
  if (typeof window !== "undefined" && window.location) {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "myfitsociety.com" || host.endsWith(".myfitsociety.com")) {
      return true;
    }
  }

  return false;
};

const setTagManager = () => {
  if (!shouldInjectGtm()) {
    return null;
  }
  const gtmId = resolveGtmContainerId();
  if (!gtmId) {
    return null;
  }
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`
      }}
    />
  );
};

export const bodyTagManager = () => {
  if (!shouldInjectGtm()) {
    return null;
  }
  const gtmId = resolveGtmContainerId();
  if (!gtmId) {
    return null;
  }
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
      />
    </noscript>
  );
};

// AdSense loader dipakai auto-ads di halaman konten (artikel/resep/dll). Tidak
// ada slot iklan di landing/funnel — memuatnya di sana hanya menambah ~90KB JS
// + main-thread cost tanpa menampilkan apa pun, jadi skip untuk pathname itu.
const ADSENSE_EXCLUDED_PATHNAMES = [
  "/",
  "/id",
  "/new-landing-page",
  "/plan",
  "/questionnaire-v2",
  "/questionnaire",
  "/cms-landing",
  "/premium/redeem-voucher",
  // Funnel steps rendered by the express server (routes/landing-page-*.js,
  // routes/cms-dynamic.js, server.js). ctx.pathname = Next page path, not the
  // request URL — quiz/summary/signin steps need their page paths listed
  // explicitly (match is exact, not prefix).
  "/promo/quiz",
  "/promo/quiz/summary",
  "/questionnaire/summary",
  "/questionnaire/price-login",
  "/questionnaire-v2/summary",
  "/collab/package-list",
  // Promo/plan landing siblings — same funnel-landing class as new-landing-page.
  "/promo2",
  "/promo3",
  "/promo6",
  "/promoprice",
  "/plan-v3",
  "/plan-v4",
  "/plan-pricing",
  "/package-list",
  "/free-premium",
  "/collab/get-plan",
  // Private/transactional flows: no ad slots, so the loader only costs ~90KB
  // of JS + two ad iframes per view (owner confirmed AdSense auto-ads are not
  // used under /auth, /payment, /reset-password). One entry per page file
  // under pages/auth, pages/payment, pages/reset-password (exact ctx.pathname).
  "/auth/account",
  "/auth/account-otp",
  "/auth/account-overview",
  "/auth/activate/[id]",
  "/auth/active-subscription",
  "/auth/affiliate",
  "/auth/auth-redirect",
  "/auth/auth-redirect-landing",
  "/auth/change-email",
  "/auth/change-name",
  "/auth/complete-registration",
  "/auth/forgot-password",
  "/auth/login",
  "/auth/login/[id]",
  "/auth/login/LoginComponent",
  "/auth/otp",
  "/auth/registration-success",
  "/auth/registration-success/access-program",
  "/payment/cleeng/[id]",
  "/payment/error-payment",
  "/payment/in-app-upgrade-success",
  "/payment/insufficient/[intent_id]",
  "/payment/purchases/[id]",
  "/payment/purchases/credit-card/[id]",
  "/payment/purchases/debit-card/[id]",
  "/payment/status",
  "/payment/success-payment",
  "/payment/transaction-expired",
  "/reset-password/[id]",
  "/reset-password/success",
];

const HeadComponent = ({
  manualOgs,
  ownsOg = false,
  enableIndexing = false,
  canonicalUrl,
  alternateUrls,
  fontsCss,
  browserThemeColor = DEFAULT_BROWSER_THEME_COLOR,
  pathname = "",
  htmlLang,
}) => (
  <Head>
    {/* Google Tag Manager */}
    {setTagManager()}
    {/* End Google Tag Manager */}

    {/* Robots — host-aware: index hanya di myfitsociety.com (produksi). */}
    {/* Staging (mfsdev.net/prod.mfsdev.net) & halaman privat tetap noindex. */}
    <meta name="robots" content={getRobotsMetaContent(enableIndexing)} />
    {enableIndexing && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    {/* hreflang EN/ID + x-default — hanya pada halaman indexable, menemani canonical. */}
    {enableIndexing && alternateUrls && (
      <Fragment>
        <link rel="alternate" hrefLang="en" href={alternateUrls.en} />
        <link rel="alternate" hrefLang="id" href={alternateUrls.id} />
        <link rel="alternate" hrefLang="x-default" href={alternateUrls.en} />
      </Fragment>
    )}
    {/* Structured data untuk search + AI engines (Organization/WebSite/App). */}
    {enableIndexing && (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: buildSiteJsonLd(canonicalUrl, htmlLang) }}
      />
    )}
    <meta charSet="utf-8" />
    {/* The single viewport meta lives in pages/_app.js (next/head), where it
        replaces Next's built-in default. Emitting one here too produced two. */}
    {/* Favicon */}
    <link rel="shortcut icon" href="/static/favicons/favicon.ico" />
    {/* favicon.svg deliberately not linked — it's a 172KB base64-PNG wrapper;
        the png/ico icons below cover every browser. */}
    <link rel="icon" type="image/png" sizes="16x16" href="/static/favicons/favicon-16x16.png" />
    <link rel="icon" type="image/png" sizes="32x32" href="/static/favicons/favicon-32x32.png" />
    <link rel="icon" type="image/png" sizes="96x96" href="/static/favicons/favicon-96x96.png" />
    <link rel="apple-touch-icon" href="/static/favicons/apple-touch-icon.png" />
    <link rel="icon" type="image/png" sizes="192x192" href="/static/favicons/web-app-manifest-192x192.png" />
    <link rel="icon" type="image/png" sizes="512x512" href="/static/favicons/web-app-manifest-512x512.png" />

    <link rel="manifest" href="/static/favicons/site.webmanifest" />
    {/* Early connect ke font CDN — menghapus ~1 rtt handshake dari critical path. */}
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    {/* slick-theme dipakai slider below-fold — defer via print->all swap supaya
        tidak render-blocking; listener native (React 16 tidak attach onLoad ke
        <link>), noscript fallback untuk JS-off. */}
    <link
      id="slick-theme-css"
      rel="stylesheet"
      href="/static/slick/slick-theme.css"
      media="print"
    />
    <script
      dangerouslySetInnerHTML={{
        __html: "var l=document.getElementById('slick-theme-css');if(l){var f=function(){l.media='all'};if(l.sheet){f()}else{l.addEventListener('load',f)}}",
      }}
    />
    <noscript>
      <link rel="stylesheet" href="/static/slick/slick-theme.css" />
    </noscript>


    <meta name="msapplication-TileColor" content="#ffffff" />
    <meta
      name="msapplication-TileImage"
      content="/static/favicons/web-app-manifest-192x192.png"
    />
    {/* PWA primary color */}
    <meta name="theme-color" content={browserThemeColor} />
    {/* Disable dark mode for Samsung browser */}
    <meta name="color-scheme" content="light" />
    <meta name="supported-color-schemes" content="light" />
    {/* Seluruh Google Fonts (Open Sans, Muli, Raleway, Poppins, Material Icons)
        di-serve dari satu stylesheet same-origin — menghapus ~1s render-block
        handshake cross-origin ke fonts.googleapis.com. Isi file = response css2
        Google apa adanya (font-display: swap, woff2 tetap di fonts.gstatic.com
        yang sudah ke-preconnect di atas). */}
    {fontsCss
      ? <style dangerouslySetInnerHTML={{ __html: fontsCss }} />
      : <link rel="stylesheet" href="/static/fonts/google-fonts.css" />}
    <style
      dangerouslySetInnerHTML={{
        __html: `
          .material-icons,
          .material-icons-outlined,
          .material-icons-round,
          .material-icons-sharp,
          .material-icons-two-tone {
            font-display: block;
          }
          .material-icons:not(.fonts-loaded-icons) {
            visibility: hidden;
          }
          .fonts-loaded-icons .material-icons,
          html.fonts-loaded-icons .material-icons {
            visibility: visible;
          }
        `
      }}
    />
    <script
      dangerouslySetInnerHTML={{
        __html: `
          (function(){
            if (typeof document === 'undefined' || !document.fonts || !document.fonts.load) {
              if (document && document.documentElement) {
                document.documentElement.classList.add('fonts-loaded-icons');
              }
              return;
            }
            var done = function(){
              if (document.documentElement) {
                document.documentElement.classList.add('fonts-loaded-icons');
              }
            };
            try {
              document.fonts.load('24px "Material Icons"').then(done).catch(done);
            } catch(e) { done(); }
            setTimeout(done, 3000);
          })();
        `
      }}
    />
    {/* Ionicons di-self-host: menghapus koneksi cross-origin (~1s render-block)
        dan CSP report-only noise (style-src 'self'). Glyph ikon dipakai halaman
        lama (bukan landing) — defer via print->all swap, noscript fallback. */}
    <link
      id="ionicons-css"
      href="/static/vendor/ionicons/2.0.1/css/ionicons.min.css"
      rel="stylesheet"
      media="print"
    />
    <script
      dangerouslySetInnerHTML={{
        __html: "var l=document.getElementById('ionicons-css');if(l){var f=function(){l.media='all'};if(l.sheet){f()}else{l.addEventListener('load',f)}}",
      }}
    />
    <noscript>
      <link href="/static/vendor/ionicons/2.0.1/css/ionicons.min.css" rel="stylesheet" />
    </noscript>
    {/*  Structural social tags — pages never override these. */}
    {!manualOgs && (
      <Fragment>
        <meta name="author" content={SITE_NAME} />
        <meta property="og:url" content={getSocialUrl(canonicalUrl)} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content={getOgLocale(resolvePageLang(canonicalUrl, htmlLang))} />
        <meta property="og:type" content={brand.mobile.ogType} />
        <meta name="twitter:site" content={TWITTER_HANDLE} />
      </Fragment>
    )}
    {/*  og:title/description/image defaults — Next 9 dedupes meta by `name`
        only, never `property`, so these defaults must be skipped on pages that
        ship their own og set (ownsOg) or the tags would double. name-keyed
        defaults (description, twitter:*) live in pages/_app.js where dedupe
        works. */}
    {!manualOgs && !ownsOg && (
      <Fragment>
        <meta property="og:title" content={brand.mobile.title} />
        <meta property="og:description" content={brand.mobile.desc} />
        <meta property="og:image" content={toAbsoluteAssetUrl(brand.mobile.ogImg)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Fragment>
    )}
    {!ADSENSE_EXCLUDED_PATHNAMES.includes(pathname) && (
      <script data-ad-client="ca-pub-5590868822967895" async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js" />
    )}
  </Head>
);

export default HeadComponent;
